import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Card, 
    CardContent, 
    Chip, 
    IconButton, 
    Grid, 
    CircularProgress, 
    Alert,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddStrategyModal from './AddStrategyModal';
import EditStrategyModal from './EditStrategyModal';
import { getAllStrategies, deleteStrategy, startStrategy, stopStrategy, getRunningStrategies, getStrategyStatus } from '../../api/strategyApi';
import { toast } from 'react-toastify';
import { useSignalRContext } from '../../SignalR/useSignalR';
import './SettingsResponsive.css';

const StrategyManagement = ({ onBack, onClose, selectedAccount }) => {
    const { clearActiveStrategy, strategyDashboardData } = useSignalRContext();
    const [strategies, setStrategies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddStrategy, setShowAddStrategy] = useState(false);
    const [formVersion, setFormVersion] = useState('v1'); // Track which form version to open
    const [showEditStrategy, setShowEditStrategy] = useState(false);
    const [selectedStrategy, setSelectedStrategy] = useState(null);
    const [editFormVersion, setEditFormVersion] = useState('v1'); // Track edit form version
    const [strategyStatuses, setStrategyStatuses] = useState({}); // Track running strategies
    const [actionLoading, setActionLoading] = useState({}); // Track loading states for actions
    const [runningStrategies, setRunningStrategies] = useState([]); // Track all running strategies

    // Fetch running strategies
    const fetchRunningStrategies = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }

            const runningData = await getRunningStrategies(token);
            const runningArray = Array.isArray(runningData) ? runningData : (runningData?.data || []);
            setRunningStrategies(runningArray);
            
            // Update strategy statuses based on running strategies
            const statusMap = {};
            runningArray.forEach(strategy => {
                statusMap[strategy.id] = 'running';
            });
            setStrategyStatuses(statusMap);
        } catch (error) {
            console.error('Error fetching running strategies:', error);
            // Don't show error toast for this, as it's not critical
        }
    };

    // Fetch real strategy status using the status endpoint
    const fetchRealStrategyStatus = async (strategiesArray) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }

            
            // Get strategy IDs
            const strategyIds = strategiesArray.map(strategy => strategy.id).filter(id => id);
            
            if (strategyIds.length === 0) {
                return;
            }

            // Get status for all strategies using the status endpoint
            const statusPromises = strategyIds.map(async (strategyId) => {
                try {
                    const response = await getStrategyStatus(token, strategyId);
                    return {
                        strategyId,
                        status: response,
                        success: true
                    };
                } catch (error) {
                    return {
                        strategyId,
                        status: { isRunning: false, status: 'unknown' },
                        success: false
                    };
                }
            });

            const results = await Promise.all(statusPromises);
            
            // Update strategy statuses
            const statusMap = {};
            results.forEach(result => {
                if (result.success && result.status) {
                    // Check if the strategy is running based on the response
                    const isRunning = result.status.isRunning === true || 
                                    result.status.status === 'running' || 
                                    result.status.status === 'Running' ||  // Capital R
                                    result.status.status === 'active' ||
                                    result.status.status === 'Active';    // Capital A
                    
                    // Handle both string and number strategy IDs
                    const strategyId = result.strategyId;
                    statusMap[strategyId] = isRunning ? 'running' : 'stopped';
                    statusMap[String(strategyId)] = isRunning ? 'running' : 'stopped';
                    statusMap[Number(strategyId)] = isRunning ? 'running' : 'stopped';
                } else {
                    const strategyId = result.strategyId;
                    statusMap[strategyId] = 'unknown';
                    statusMap[String(strategyId)] = 'unknown';
                    statusMap[Number(strategyId)] = 'unknown';
                }
            });

            // Merge with existing statuses instead of replacing
            setStrategyStatuses(prev => ({ ...prev, ...statusMap }));

        } catch (error) {
            // Don't show error toast for this, as it's not critical
        }
    };

    // Fetch strategies from API
    const fetchStrategies = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token not found');
            }

            const data = await getAllStrategies(token);
            // Handle API response structure
            const strategiesArray = Array.isArray(data) ? data : (data?.data || []);

            // ✅ FILTER STRATEGIES BY SELECTED ACCOUNT NUMBER
            // Only show strategies where accountNumber matches selectedAccount
            const filteredStrategies = strategiesArray.filter(strategy => {
                const strategyAccountNumber = strategy.accountNumber;

                // Skip if strategy has no account number
                if (!strategyAccountNumber) {
                    console.warn('⚠️ Strategy excluded - no account number:', {
                        strategyId: strategy.id,
                        strategyName: strategy.name,
                        accountNumber: strategyAccountNumber
                    });
                    return false;
                }

                // Skip if no account is selected
                if (!selectedAccount) {
                    console.warn('⚠️ Strategy excluded - no account selected');
                    return false;
                }

                // Compare account numbers (convert to string for safe comparison)
                const strategyAccNum = String(strategyAccountNumber);
                const selectedAccNum = String(selectedAccount);
                const matches = strategyAccNum === selectedAccNum;

                if (!matches) {
                    console.log('🔍 Strategy filtered out - account mismatch:', {
                        strategyId: strategy.id,
                        strategyName: strategy.name,
                        strategyAccount: strategyAccNum,
                        selectedAccount: selectedAccNum
                    });
                }

                return matches;
            });

            console.log('📊 Strategy Filtering Results:', {
                totalStrategies: strategiesArray.length,
                filteredStrategies: filteredStrategies.length,
                selectedAccount: selectedAccount,
                filtered: filteredStrategies.map(s => ({
                    id: s.id,
                    name: s.name,
                    accountNumber: s.accountNumber
                }))
            });

            setStrategies(filteredStrategies);

            // Get real status for each strategy using the status endpoint
            await fetchRealStrategyStatus(filteredStrategies);
        } catch (error) {
            console.error('Error fetching strategies:', error);
            setError(error.message || 'Failed to fetch strategies');
            toast.error('Failed to load strategies');
        } finally {
            setLoading(false);
        }
    };

    // Load strategies when component mounts or selectedAccount changes
    useEffect(() => {
        fetchStrategies();
    }, [selectedAccount]); // ✅ Re-fetch when account changes

    const handleAddStrategyV1 = () => {
        setFormVersion('v1');
        setShowAddStrategy(true);
    };

    const handleAddStrategyV2 = () => {
        setFormVersion('v2');
        setShowAddStrategy(true);
    };

    const handleEditStrategy = (strategy) => {
        // Detect version from parameters
        const version = strategy?.parameters?.version || 'v1';

        setEditFormVersion(version);
        setSelectedStrategy(strategy);
        setShowEditStrategy(true);
    };

    const handleDeleteStrategy = async (strategy) => {
        if (window.confirm(`Are you sure you want to delete "${strategy.name}"? This action cannot be undone.`)) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('Authentication token not found');
                    return;
                }

                await deleteStrategy(token, strategy.id);
                toast.success(`Strategy "${strategy.name}" deleted successfully!`);
                // Refresh strategies list
                fetchStrategies();
            } catch (error) {
                console.error('Error deleting strategy:', error);
                toast.error('Failed to delete strategy');
            }
        }
    };

    const handleAddStrategySuccess = (newStrategy) => {
        fetchStrategies(); // Refresh the list
    };

    const handleEditStrategySuccess = (updatedStrategy) => {
        fetchStrategies(); // Refresh the list
    };

    const handleStartStrategy = async (strategy) => {
        try {
            setActionLoading(prev => ({ ...prev, [strategy.id]: true }));
            
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found');
                return;
            }

            await startStrategy(token, strategy.id);
            
            // Update strategy status to running
            setStrategyStatuses(prev => ({ ...prev, [strategy.id]: 'running' }));
            toast.success(`Strategy "${strategy.name}" started successfully!`);
            
            // Refresh real strategy status
            await fetchRealStrategyStatus([strategy]);
            
        } catch (error) {
            console.error('Error starting strategy:', error);
            toast.error(`Failed to start strategy "${strategy.name}"`);
        } finally {
            setActionLoading(prev => ({ ...prev, [strategy.id]: false }));
        }
    };

    const handleStopStrategy = async (strategy) => {
        try {
            setActionLoading(prev => ({ ...prev, [strategy.id]: true }));

            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Authentication token not found');
                return;
            }

            await stopStrategy(token, strategy.id);

            // Check if this is the currently active strategy
            const currentStrategyId = strategyDashboardData?.id ||
                                      strategyDashboardData?.strategyId ||
                                      strategyDashboardData?.StrategyId ||
                                      strategyDashboardData?.strategyID;

            if (currentStrategyId && String(currentStrategyId) === String(strategy.id)) {
                // Clear active strategy so next one can show automatically
                clearActiveStrategy();
            }

            // Update strategy status to stopped
            setStrategyStatuses(prev => ({ ...prev, [strategy.id]: 'stopped' }));
            toast.success(`Strategy "${strategy.name}" stopped successfully!`);

            // Refresh real strategy status
            await fetchRealStrategyStatus([strategy]);

        } catch (error) {
            console.error('Error stopping strategy:', error);
            toast.error(`Failed to stop strategy "${strategy.name}"`);
        } finally {
            setActionLoading(prev => ({ ...prev, [strategy.id]: false }));
        }
    };

    // Filter strategies based on search term and sort by ID descending (newest first)
    const filteredStrategies = strategies
        .filter(strategy =>
            strategy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            strategy.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            strategy.strategyType?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => (b.id || 0) - (a.id || 0)); // Sort by ID descending (newest first)

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return '#1de9b6';
            case 'inactive':
                return '#ff6b6b';
            case 'paused':
                return '#ffd600';
            default:
                return '#bfc9db';
        }
    };

    // Helper function to get strategy type color
    const getStrategyTypeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'scalping':
                return '#ff6b6b';
            case 'swing':
                return '#1de9b6';
            case 'day trading':
                return '#00cfff';
            case 'position':
                return '#ffd600';
            default:
                return '#9c27b0';
        }
    };

    // Helper function to check if strategy is running
    const isStrategyRunning = (strategy) => {
        const strategyId = strategy.id;
        const result = strategyStatuses[strategyId] === 'running' ||
               strategyStatuses[String(strategyId)] === 'running' ||
               strategyStatuses[Number(strategyId)] === 'running';

        return result;
    };

    // Helper function to get action button for strategy
    const getActionButton = (strategy) => {
        const isLoading = actionLoading[strategy.id];
        const isRunning = isStrategyRunning(strategy);
        
        if (isLoading) {
            return (
                <Button
                    size="small"
                    disabled
                    sx={{
                        minWidth: '60px',
                        height: '24px',
                        fontSize: '10px',
                        textTransform: 'none',
                        borderRadius: '4px'
                    }}
                >
                    <CircularProgress size={12} sx={{ color: '#bfc9db' }} />
                </Button>
            );
        }
        
        if (isRunning) {
            return (
                <Button
                    onClick={() => handleStopStrategy(strategy)}
                    size="small"
                    variant="contained"
                    sx={{
                        backgroundColor: '#ff6b6b',
                        color: 'white',
                        minWidth: '60px',
                        height: '24px',
                        fontSize: '10px',
                        textTransform: 'none',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#ff5252'
                        }
                    }}
                    title="Stop Strategy"
                >
                    Stop
                </Button>
            );
        } else {
            return (
                <Button
                    onClick={() => handleStartStrategy(strategy)}
                    size="small"
                    variant="contained"
                    sx={{
                        backgroundColor: '#1de9b6',
                        color: 'white',
                        minWidth: '60px',
                        height: '24px',
                        fontSize: '10px',
                        textTransform: 'none',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#00e676'
                        }
                    }}
                    title="Start Strategy"
                >
                    Start
                </Button>
            );
        }
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{
                p: 3,
                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(6, 182, 212, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onBack} sx={{ color: '#fff' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <PsychologyIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                    <Box>
                        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                            Strategy Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                            Manage trading strategies and configurations
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchStrategies}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddStrategyV1}
                        sx={{
                            background: '#ff9800',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#f57c00'
                            }
                        }}
                    >
                        Create Strategy V1
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddStrategyV2}
                        sx={{
                            background: '#1de9b6',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#00e676'
                            }
                        }}
                    >
                        Create Strategy V2
                    </Button>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#ff6b6b',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                color: '#ff4444'
                            }
                        }}
                        title="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                {/* Search Bar */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search strategies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#bfc9db' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                '&.Mui-focused fieldset': { borderColor: '#ff9800' }
                            },
                            '& .MuiInputBase-input::placeholder': { color: '#bfc9db' }
                        }}
                    />
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress sx={{ color: '#ff9800' }} />
                        <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                            Loading strategies...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 3,
                            background: 'rgba(211, 47, 47, 0.1)',
                            border: '1px solid #d32f2f',
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#ff6b6b' }
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Strategies Table */}
                {!loading && !error && (
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                            <Checkbox sx={{ color: '#bfc9db' }} />
                                        </TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Name</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Type</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Status</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Running</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Symbols</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Enabled</TableCell>
                                        <TableCell sx={{ color: '#bfc9db', fontWeight: 600, borderBottom: '1px solid rgba(6, 182, 212, 0.2)' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredStrategies.length > 0 ? (
                                        filteredStrategies.map((strategy) => (
                                            <TableRow key={strategy.id} sx={{ '&:hover': { background: 'rgba(6, 182, 212, 0.1)' } }}>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Checkbox sx={{ color: '#bfc9db' }} />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                            {strategy.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#bfc9db' }}>
                                                            {strategy.description}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Chip
                                                        label={strategy.strategyType || 'Unknown'}
                                                        size="small"
                                                        sx={{
                                                            background: getStrategyTypeColor(strategy.strategyType),
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Chip
                                                        label={strategy.status || 'Unknown'}
                                                        size="small"
                                                        sx={{
                                                            background: getStatusColor(strategy.status),
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Chip
                                                        label={isStrategyRunning(strategy) ? 'Running' : 'Stopped'}
                                                        size="small"
                                                        sx={{
                                                            background: isStrategyRunning(strategy) ? '#1de9b6' : '#ff6b6b',
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Typography variant="body2" sx={{ color: '#fff' }}>
                                                        {strategy.symbols?.length || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Chip
                                                        label={strategy.isEnabled ? 'Yes' : 'No'}
                                                        size="small"
                                                        sx={{
                                                            background: strategy.isEnabled ? '#1de9b6' : '#ff6b6b',
                                                            color: '#000',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        {/* Start/Stop Button */}
                                                        {getActionButton(strategy)}
                                                        
                                                        {/* Edit Button */}
                                                        <IconButton
                                                            onClick={() => handleEditStrategy(strategy)}
                                                            size="small"
                                                            sx={{
                                                                color: '#00cfff',
                                                                '&:hover': {
                                                                    background: 'rgba(0, 207, 255, 0.1)',
                                                                    color: '#1de9b6'
                                                                }
                                                            }}
                                                            title="Edit Strategy"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        
                                                        {/* Delete Button */}
                                                        <IconButton
                                                            onClick={() => handleDeleteStrategy(strategy)}
                                                            size="small"
                                                            sx={{
                                                                color: '#ff6b6b',
                                                                '&:hover': {
                                                                    background: 'rgba(255, 107, 107, 0.1)',
                                                                    color: '#ff5252'
                                                                }
                                                            }}
                                                            title="Delete Strategy"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography variant="body1" sx={{ color: '#bfc9db' }}>
                                                    No strategies found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </Box>

            {/* Status Bar */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(6, 182, 212, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#ff9800' }}>🧠</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        Total Strategies: {Array.isArray(strategies) ? strategies.length : 0}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#1de9b6' }}>✅</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        Enabled: {Array.isArray(strategies) ? strategies.filter(s => s.isEnabled).length : 0}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>🕐</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        {new Date().toLocaleTimeString()}
                    </Typography>
                </Box>
            </Box>

            {/* Child Modals - Render outside of main modal */}
            <AddStrategyModal
                open={showAddStrategy}
                onClose={() => setShowAddStrategy(false)}
                onSuccess={handleAddStrategySuccess}
                selectedAccount={selectedAccount}
                formVersion={formVersion}
            />

            <EditStrategyModal
                open={showEditStrategy}
                onClose={() => setShowEditStrategy(false)}
                onSuccess={handleEditStrategySuccess}
                strategy={selectedStrategy}
                formVersion={editFormVersion}
                selectedAccount={selectedAccount}
            />
        </Box>
    );
};

export default StrategyManagement;
